import React, { useState } from 'react';
import { CreditCardIcon, UserIcon, CalendarIcon, TagIcon, ClockIcon, CheckCircleIcon, XCircleIcon, FileTextIcon, SearchIcon, FilterIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
export function ExpenseApproval({
  locale = 'en-US'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  // Format currency based on locale
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Sample data for expenses needing approval
  const pendingExpenses = [{
    id: 'EXP-2023-007',
    title: 'Customer Workshop',
    date: '2023-11-15',
    submittedDate: '2023-11-16',
    amount: 1280.5,
    category: 'Events',
    submittedBy: 'Sarah Williams',
    department: 'Marketing',
    description: 'Expenses for hosting a customer workshop including venue rental, catering, and materials.',
    items: [{
      description: 'Venue Rental',
      amount: 750.0,
      receipt: true
    }, {
      description: 'Catering',
      amount: 350.0,
      receipt: true
    }, {
      description: 'Workshop Materials',
      amount: 180.5,
      receipt: true
    }]
  }, {
    id: 'EXP-2023-008',
    title: 'Trade Show Attendance',
    date: '2023-11-08',
    submittedDate: '2023-11-12',
    amount: 2450.75,
    category: 'Marketing',
    submittedBy: 'Robert Brown',
    department: 'Marketing',
    description: 'Expenses for attending the annual industry trade show including flights, accommodation, and registration fees.',
    items: [{
      description: 'Flight',
      amount: 850.0,
      receipt: true
    }, {
      description: 'Hotel (3 nights)',
      amount: 675.0,
      receipt: true
    }, {
      description: 'Registration Fee',
      amount: 599.0,
      receipt: true
    }, {
      description: 'Transportation',
      amount: 125.75,
      receipt: true
    }, {
      description: 'Meals',
      amount: 201.0,
      receipt: true
    }]
  }, {
    id: 'EXP-2023-009',
    title: 'Client Meeting - XYZ Inc',
    date: '2023-11-10',
    submittedDate: '2023-11-11',
    amount: 187.25,
    category: 'Travel',
    submittedBy: 'John Doe',
    department: 'Sales',
    description: 'Expenses for meeting with potential client XYZ Inc including travel and lunch.',
    items: [{
      description: 'Taxi',
      amount: 42.5,
      receipt: true
    }, {
      description: 'Lunch',
      amount: 78.75,
      receipt: true
    }, {
      description: 'Printed Materials',
      amount: 36.0,
      receipt: true
    }, {
      description: 'Return Taxi',
      amount: 30.0,
      receipt: true
    }]
  }];
  // Filter expenses based on search term
  const filteredExpenses = pendingExpenses.filter(expense => {
    return searchTerm === '' || expense.title.toLowerCase().includes(searchTerm.toLowerCase()) || expense.id.toLowerCase().includes(searchTerm.toLowerCase()) || expense.submittedBy.toLowerCase().includes(searchTerm.toLowerCase()) || expense.department.toLowerCase().includes(searchTerm.toLowerCase());
  });
  // Toggle expanded state for an expense item
  const toggleExpand = id => {
    setExpandedItems({
      ...expandedItems,
      [id]: !expandedItems[id]
    });
  };
  // Handle approve action
  const handleApprove = (id, e) => {
    e.stopPropagation();
    // In a real app, this would call an API
    alert(`Expense ${id} approved successfully!`);
  };
  // Handle reject action
  const handleReject = (id, e) => {
    e.stopPropagation();
    // In a real app, this would show a modal for rejection reason
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      alert(`Expense ${id} rejected. Reason: ${reason}`);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-7409">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7410">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7411">
          Expenses Pending Your Approval
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-7412">
          Review and approve or reject expense claims
        </p>
      </div>
      {/* Search */}
      <div className="p-4 border-b border-gray-200 sm:p-6" data-id="element-7413">
        <div className="max-w-lg w-full" data-id="element-7414">
          <div className="relative rounded-md shadow-sm" data-id="element-7415">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7416">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-7417" />
            </div>
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search by title, name, or department..." data-id="element-7418" />
          </div>
        </div>
      </div>
      {/* Expense list */}
      <ul className="divide-y divide-gray-200" data-id="element-7419">
        {filteredExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-7420">
            No expenses pending your approval.
          </li> : filteredExpenses.map(expense => <li key={expense.id} className="block" data-id="element-7421">
              <div className={`px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer ${expandedItems[expense.id] ? 'bg-gray-50' : ''}`} onClick={() => toggleExpand(expense.id)} data-id="element-7422">
                <div className="flex items-center justify-between" data-id="element-7423">
                  <div className="flex items-center space-x-3" data-id="element-7424">
                    <div className="flex-shrink-0" data-id="element-7425">
                      <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-7426" />
                    </div>
                    <div data-id="element-7427">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-7428">
                        {expense.title}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-7429">
                        {expense.id} • Submitted{' '}
                        {formatDate(expense.submittedDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-7430">
                    <p className="text-sm font-medium text-gray-900" data-id="element-7431">
                      {formatCurrency(expense.amount)}
                    </p>
                    <ChevronDownIcon className={`h-5 w-5 text-gray-400 transform transition-transform ${expandedItems[expense.id] ? 'rotate-180' : ''}`} data-id="element-7432" />
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between" data-id="element-7433">
                  <div className="sm:flex" data-id="element-7434">
                    <p className="flex items-center text-sm text-gray-500" data-id="element-7435">
                      <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-7436" />
                      {expense.submittedBy} • {expense.department}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-7437">
                    <TagIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-7438" />
                    <p data-id="element-7439">{expense.category}</p>
                  </div>
                </div>
                {/* Expanded content */}
                {expandedItems[expense.id] && <div className="mt-4 border-t border-gray-200 pt-4" data-id="element-7440">
                    <div className="mb-4" data-id="element-7441">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-7442">
                        Description
                      </h4>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-7443">
                        {expense.description}
                      </p>
                    </div>
                    <div className="mb-4" data-id="element-7444">
                      <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-7445">
                        Expense Items
                      </h4>
                      <div className="overflow-x-auto" data-id="element-7446">
                        <table className="min-w-full divide-y divide-gray-200" data-id="element-7447">
                          <thead className="bg-gray-50" data-id="element-7448">
                            <tr data-id="element-7449">
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7450">
                                Description
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7451">
                                Amount
                              </th>
                              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7452">
                                Receipt
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200" data-id="element-7453">
                            {expense.items.map((item, index) => <tr key={index} data-id="element-7454">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-7455">
                                  {item.description}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-7456">
                                  {formatCurrency(item.amount)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-7457">
                                  {item.receipt ? <button className="text-indigo-600 hover:text-indigo-900" data-id="element-7458">
                                      View
                                    </button> : 'None'}
                                </td>
                              </tr>)}
                          </tbody>
                          <tfoot data-id="element-7459">
                            <tr className="bg-gray-50" data-id="element-7460">
                              <th scope="row" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7461">
                                Total
                              </th>
                              <td colSpan={2} className="px-6 py-3 text-right text-sm font-medium text-gray-900" data-id="element-7462">
                                {formatCurrency(expense.amount)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3" data-id="element-7463">
                      <button onClick={e => handleReject(expense.id, e)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-7464">
                        <XCircleIcon className="mr-2 -ml-1 h-5 w-5 text-red-500" data-id="element-7465" />
                        Reject
                      </button>
                      <button onClick={e => handleApprove(expense.id, e)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7466">
                        <CheckCircleIcon className="mr-2 -ml-1 h-5 w-5 text-white" data-id="element-7467" />
                        Approve
                      </button>
                    </div>
                  </div>}
              </div>
            </li>)}
      </ul>
    </div>;
}